<template>
  <div style="position: relative">
    <div class="d-flex align-items-center">
      <b-form-select
        class="flex-1"
        :modelValue="operator"
        @update:modelValue="updateOperator"
        :options="options"
        size="sm"
        style="width: auto"
      />
    </div>
    <div
      class="mt-1"
      v-if="operator !== 'isnull' && operator !== 'isnotnull' && selections?.length > 0"
    >
      <multiselect
        v-if="selections.length > 20"
        :allow-empty="true"
        :show-labels="true"
        select-label="選択"
        placeholder="選択"
        :model-value="formattedValue"
        :internal-search="false"
        :options="keywordFilteredSelections"
        :multiple="true"
        label="label"
        track-by="value"
        @open="searchChange"
        @update:modelValue="change"
        @search-change="searchChange"
      >
        <template #noOptions> --- </template>
        <template #noResult> --- </template>
      </multiselect>
      <b-form-radio-group
        v-else-if="shouldSingleSelect"
        :model-value="formattedValue?.[0]"
        @update:modelValue="(value) => change([value])"
        :options="selections"
        text-field="label"
        value-field="value"
      />
      <b-form-checkbox-group
        v-else
        :model-value="formattedValue"
        @update:modelValue="change"
      >
        <b-form-checkbox
          v-for="option in selections"
          :key="option"
          :value="selectionValue(option)"
        >
          {{ selectionLabel(option) }}
        </b-form-checkbox>
      </b-form-checkbox-group>
    </div>
  </div>
</template>
<script lang="ts">
import { FilterItemService } from '../../../FilterItemService'
import { FILTER_OPERATOR_FOR_SELECT } from '../../../FilterRuleService'

export default {
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      operator: null,
      selections: [],
      keyword: '',
    }
  },
  computed: {
    filterItemService(): FilterItemService<any> {
      // @ts-ignore
      return (
        this.$provides?.filterItemService?.value || this.$provides?.filterObject?.value
      )
    },
    label() {
      return this.filterItemService.colDef.label || this.filterItemService.colDef.name
    },
    options() {
      // { value: xxxx'', text: 'xxxxx' }のような形で返す
      return Object.keys(FILTER_OPERATOR_FOR_SELECT).reduce((res, operator) => {
        res.push({
          value: operator,
          text: FILTER_OPERATOR_FOR_SELECT[operator],
        })
        return res
      }, [])
    },
    formattedValue() {
      const colName = this.filterItemService.colDef.name
      let uuids = []

      // Extract UUIDs from stored format
      if (this.modelValue?.length > 0 && typeof this.modelValue?.[0] === 'object') {
        uuids = this.modelValue?.map?.((v) => Object.values(v[colName])[0]) || []
        if (this.isMultiSelect && this.operator === 'nin') {
          uuids = uuids.slice(-1)
        }
      } else {
        uuids = Array.isArray(this.modelValue) ? this.modelValue : []
      }

      // For multiselect (> 20 items), convert UUIDs back to {label, value} objects
      if (this.selections.length > 20) {
        return uuids.map(
          (uuid) => this.selections.find((s) => this.selectionValue(s) === uuid) || uuid,
        )
      }

      // For radio/checkbox groups, return UUIDs as-is
      return uuids
    },
    isMultiSelect() {
      return this.filterItemService.colDef.type === 'MULTISELECT'
    },
    shouldSingleSelect() {
      return this.isMultiSelect && this.operator === 'nin'
    },
    keywordFilteredSelections() {
      if (!this.keyword) {
        return this.selections
      }
      return this.selections.filter((v) =>
        `${v.label}`.toLowerCase().includes(this.keyword),
      )
    },
  },
  watch: {
    'filterItemService.colDef.name'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$nextTick(() => {
          this.init()
        })
      }
    },
  },
  mounted() {
    this.init()
  },
  errorCaptured(err, vm, info) {
    console.error(`[FilterItemSelect] errorCaptured: `, {
      FilterItemSelectComponent: this,
      err,
      vm,
      info,
    })
    return false
  },
  methods: {
    async init() {
      const op = this.filterItemService?.value?.operator || this.options[0].value
      if (this.filterItemService.colDef.type === 'MULTISELECT') {
        const currentValueOp = this.filterItemService?.value?.value?.[0]
        const currentOp = currentValueOp
          ? Object.keys(currentValueOp?.[this.filterItemService.colDef.name] || {})?.[0]
          : null
        if (currentOp === '_ncontains') {
          this.operator = 'nin'
        } else if (currentOp === '_contains') {
          this.operator = 'in'
        } else {
          this.operator = op
        }
      } else {
        this.operator = this.filterItemService?.value?.operator || this.options[0].value
      }
      this.setSelections()
    },
    change(value) {
      if (this.filterItemService.colDef.type === 'MULTISELECT') {
        const colName = this.filterItemService.colDef.name
        if (!value?.length) {
          this.$emit('update:modelValue', { value: null, operator: this.operator })
          return
        }
        if (this.operator === 'in') {
          this.$emit('update:modelValue', {
            value: value.map((v) => ({
              [colName]: { _contains: this.selectionValue(v) },
            })),
            operator: 'or',
          })
        } else {
          // 最後に追加されたものだけを適用する
          const lastValue = this.selectionValue(value.slice(-1)[0])
          this.$emit('update:modelValue', {
            value: [{ [colName]: { _ncontains: lastValue } }],
            operator: 'or',
          })
        }
        return
      }
      this.$emit('update:modelValue', {
        value: value?.length > 0 ? value.map((v) => this.selectionValue(v)) : null,
        operator: this.operator,
      })
    },
    selectionValue(item) {
      if (item.value) {
        return item.value
      } else {
        return item
      }
    },
    selectionLabel(item) {
      return item?.label || `${item}`
    },
    searchChange(query) {
      this.keyword = query
    },
    async setSelections() {
      const list = await this.filterItemService.getSelections()
      if (list) {
        // string[]の場合は{value, label}[]にフォーマット
        // これにより、multiselectやkeywordFilteredSelectionsで正しく動作する
        if (list.length > 0 && typeof list[0] === 'string') {
          this.selections = list.map((item) => ({ value: item, label: item }))
        } else {
          this.selections = list
        }
      }
    },
    updateOperator(value) {
      this.operator = value
      this.$emit('update:operator', this.operator)
    },
    onDismissDropDown() {
      this.$emit('onDismissDropdown')
    },
  },
}
</script>
